import { memo, useState, useEffect, useRef } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { RoutePath } from 'consts/RoutePath';
import classes from './override.module.scss';
import { useRegisterMember } from 'hooks/UseRegisterMember';
import { CircularProgress } from '@material-ui/core';
import { convertHalfToFull, convertFullToHalf, convertFullKana } from 'common/CharacterConverter';
import { createBirthYearSelectValues } from 'common/birthYearCreator';
import { FormErrors, initialFormErrors, getErrorMessage } from 'validators/RegisterMemberValidator';
import { MemberForm } from './MemberUpdateTemplate';
import { GroupAuth } from 'consts/GroupAuthConst';
import { Hr } from 'components/atoms/Hr';
import { findSexTypeNameByKey } from 'consts/SexConst';
import { initialUserData } from 'requests/RequestUser';
import {
	MESSAGE_PASSWORD_ANNOTATION,
	POPOVER_MESSAGE_PASSWORD_ANNOTATION,
	TARGET_MESSAGE_PASSWORD_POPOVER,
} from 'utils/messages';
import { PartialdAnnotation } from 'components/molecules/partial-annotation/PartialAnnotation';

type Props = {};
export type MemberInputDto = {
	groupId: string;
};
export type RegisterMemberForm = MemberForm & {
	password_confirm: string;
};

export const MemberInputTemplate = memo((_props: Props) => {
	const history = useHistory<MemberInputDto>();
	const location = useLocation<{ fromPage?: string }>();
	const fromPage =
		typeof location.state === 'undefined' || typeof location.state.fromPage === 'undefined'
			? ''
			: location.state.fromPage;
	const dto = history.location.state;
	const [memberFormData, setMemberFormData] = useState<RegisterMemberForm>({
		...initialUserData,
		sex: undefined,
		pass: '',
		password_confirm: '',
		birthYear: '',
		birthMonth: '',
		birthDate: '',
	});
	const isMen = memberFormData.sex === 0;
	const isWomen = memberFormData.sex === 1;
	const [formErrors, setFormErrors] = useState<FormErrors>(initialFormErrors);
	const isFormError = !!Object.values(formErrors).find((e) => e !== '');
	const [actionError, setActionError] = useState('');
	const isActionError = actionError !== '';
	const [page, setPage] = useState(0);

	// パスワード注釈
	const splitText = new RegExp('(' + TARGET_MESSAGE_PASSWORD_POPOVER + ')');
	const annotationMessage = MESSAGE_PASSWORD_ANNOTATION.split(splitText);

	// 各入力欄のonChangeで呼ぶ
	const handleChangeValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setMemberFormData((prev) => ({ ...prev, [event.target.name]: event.target.value }));
	};
	const handleChangeRadioValue = (event: React.ChangeEvent<HTMLInputElement>) => {
		setMemberFormData((prev) => ({ ...prev, [event.target.name]: Number(event.target.value) }));
	};
	const [isPlaceHolders, setIsPlaceHolders] = useState({
		birthYear: !memberFormData.birthYear,
		birthMonth: !memberFormData.birthMonth,
		birthDate: !memberFormData.birthDate,
	});
	const handleChangeSelectValueWithPlaceHolder = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setIsPlaceHolders((prev) => ({ ...prev, [event.target.name]: !event.target.value }));
		setMemberFormData((prev) => ({ ...prev, [event.target.name]: event.target.value }));
	};
	const handleBlurConvertHalfToFullValue = (event: React.FocusEvent<HTMLInputElement>) => {
		setMemberFormData((prev) => ({ ...prev, [event.target.name]: convertHalfToFull(event.target.value) }));
	};
	const handleBlurConvertFullToHalfValue = (event: React.FocusEvent<HTMLInputElement>) => {
		setMemberFormData((prev) => ({ ...prev, [event.target.name]: convertFullToHalf(event.target.value) }));
	};
	const handleBlurConvertFullKanaValue = (event: React.FocusEvent<HTMLInputElement>) => {
		setMemberFormData((prev) => ({ ...prev, [event.target.name]: convertFullKana(event.target.value) }));
	};

	const [processLoading, setProcessLoading] = useState(false);
	const { validate, register, back } = useRegisterMember(
		formErrors,
		setFormErrors,
		getErrorMessage,
		actionError,
		setActionError,
		memberFormData,
		setProcessLoading,
		setPage,
		dto,
		fromPage,
	);

	const scrollRef = useRef(null);
	useEffect(() => {
		scrollRef.current.scrollIntoView();
		document.title = `Gakken${GroupAuth.MEMBER.name}の登録`;
	}, [page]);

	const errorRef = useRef(null);
	useEffect(() => {
		if (isFormError || isActionError) {
			errorRef.current.scrollIntoView();
		}
	}, [formErrors, actionError]);

	return (
		<main>
			<section className="section mypage" ref={scrollRef}>
				<div className="breadcrumb">
					<ul>
						<li>
							<Link to={RoutePath.MY_PAGE}>マイページTOP</Link>
						</li>
						<li>
							<Link to={RoutePath.FAMILY_INFO}>家族情報</Link>
						</li>
						<li>
							<Link to={{ pathname: RoutePath.USER_ADD, state: dto }}>ユーザーの追加</Link>
						</li>
						<li>{`Gakken${GroupAuth.MEMBER.name}の登録`}</li>
					</ul>
				</div>
				{page === 0 && (
					<>
						<h1 className="page-head">{`Gakken${GroupAuth.MEMBER.name}の登録`}</h1>
						<div className="section-in" style={{ marginTop: 50 }}>
							<div className="section-head">
								<span>必要情報の入力</span>
							</div>
							<div className="section-txt-box">
								<div className={classes.memberInputSectionTxt}>
									<div>メールアドレスを使わずにIDを発行することができます。</div>
									<div>下記フォームに必要事項を入力し、「確認」ボタンを押してください。</div>
									<div>※サービス利用時に決済ができないなど、一部機能に制限があります</div>
								</div>
							</div>
							{isFormError || isActionError ? (
								<div className="error-box" ref={errorRef}>
									{isActionError ? <p>{actionError}</p> : null}
									{isFormError ? (
										<>
											<p>※下記項目は全て必須となります。</p>
											<div className="error-box-in">
												{formErrors.gid !== '' ? <p>【GakkenID】{formErrors.gid}</p> : null}
												{formErrors.pass !== '' ? <p>【パスワード】{formErrors.pass}</p> : null}
												{formErrors.password_confirm !== '' ? (
													<p>【パスワード（確認）】{formErrors.password_confirm}</p>
												) : null}
												{formErrors.lastname !== '' || formErrors.firstname !== '' ? (
													<p>【お名前】{formErrors.lastname || formErrors.firstname}</p>
												) : null}
												{formErrors.kana_lastname !== '' || formErrors.kana_firstname !== '' ? (
													<p>【お名前（カナ）】{formErrors.kana_lastname || formErrors.kana_firstname}</p>
												) : null}
												{formErrors.birthYear !== '' || formErrors.birthMonth !== '' || formErrors.birthDate !== '' ? (
													<p>【生年月日】{formErrors.birthYear || formErrors.birthMonth || formErrors.birthDate}</p>
												) : null}
												{formErrors.sex !== '' ? <p>【性別】{formErrors.sex}</p> : null}
											</div>
										</>
									) : null}
								</div>
							) : null}
							<div className="form-box">
								<div className="form-head">
									<label htmlFor="gid">GakkenID</label>
									<span className="required">必須</span>
									<span className="annotation">※半角英数8文字以上</span>
								</div>
								<div className="form-input width50">
									<input
										id="gid"
										type="text"
										name="gid"
										placeholder="gakken1946"
										value={memberFormData.gid}
										onChange={handleChangeValue}
										onBlur={handleBlurConvertFullToHalfValue}
									/>
								</div>
								<p className="error">{formErrors.gid}</p>
							</div>

							<div className="form-box">
								<div className={`form-head ${classes.displayFlex}`}>
									<div className={classes.newPasswordLabelBox}>
										<div className={classes.newPasswordLabel}>
											<label htmlFor="password">パスワード</label>
											<span className="required">必須</span>
										</div>
									</div>
									<div>
										<span className="annotation">
											<PartialdAnnotation
												headerMessage={annotationMessage[0]}
												footerMessage={annotationMessage[2]}
												targetMessage={annotationMessage[1]}
												popoverMessage={POPOVER_MESSAGE_PASSWORD_ANNOTATION}
												isBreakBeforeTargetText={true}
											/>
										</span>
									</div>
								</div>
								<div className="form-input width50">
									<input
										id="password"
										type="password"
										placeholder="********"
										name="pass"
										value={memberFormData.pass}
										onChange={handleChangeValue}
									/>
								</div>
								<p className="error">{formErrors.pass}</p>
							</div>
							<div className="form-box">
								<div className="form-head">
									<label htmlFor="password_confirm">パスワード（確認）</label>
									<span className="required">必須</span>
									<span className="annotation">※半角英数8文字以上</span>
								</div>
								<div className="form-input width50">
									<input
										id="password_confirm"
										type="password"
										placeholder="********"
										name="password_confirm"
										value={memberFormData.password_confirm}
										onChange={handleChangeValue}
									/>
								</div>
								<p className="error">{formErrors.password_confirm}</p>
							</div>
							<div className="form-box">
								<div className="form-head">
									<label htmlFor="lastname">お名前（全角）</label>
									<span className="required">必須</span>
								</div>
								<div className="form-input width50">
									<input
										id="lastname"
										type="text"
										name="lastname"
										placeholder="姓"
										value={memberFormData.lastname}
										onChange={handleChangeValue}
										onBlur={handleBlurConvertHalfToFullValue}
									/>
								</div>
								<p className="error">{formErrors.lastname}</p>
								<div className="form-input width50">
									<input
										type="text"
										name="firstname"
										placeholder="名"
										value={memberFormData.firstname}
										onChange={handleChangeValue}
										onBlur={handleBlurConvertHalfToFullValue}
									/>
								</div>
								<p className="error">{formErrors.firstname}</p>
							</div>
							<div className="form-box">
								<div className="form-head">
									<label htmlFor="kana_lastname">お名前（カナ）</label>
								</div>
								<div className="form-input width50">
									<input
										id="kana_lastname"
										type="text"
										name="kana_lastname"
										placeholder="セイ"
										value={memberFormData.kana_lastname}
										onChange={handleChangeValue}
										onBlur={handleBlurConvertFullKanaValue}
									/>
								</div>
								<p className="error">{formErrors.kana_lastname}</p>
								<div className="form-input width50">
									<input
										type="text"
										name="kana_firstname"
										placeholder="メイ"
										value={memberFormData.kana_firstname}
										onChange={handleChangeValue}
										onBlur={handleBlurConvertFullKanaValue}
									/>
								</div>
								<p className="error">{formErrors.kana_firstname}</p>
							</div>
							<div className="form-box">
								<div className="form-head">
									<label htmlFor="birthYear">生年月日</label>
									<span className="required">必須</span>
								</div>
								<div className="form-input width50">
									<div className="select-box">
										<select
											id="birthYear"
											name="birthYear"
											className={isPlaceHolders.birthYear ? classes.placeholderOption : ''}
											value={memberFormData.birthYear}
											onChange={handleChangeSelectValueWithPlaceHolder}
										>
											<option value="" className={classes.placeholderOption}>
												年
											</option>
											{createBirthYearSelectValues().map((v) => {
												return (
													<option value={`${v}`} className={classes.defaultOption}>
														{v}
													</option>
												);
											})}
										</select>
									</div>
								</div>
								<p className="error">{formErrors.birthYear}</p>
								<div className="form-input width50">
									<div className="select-box">
										<label htmlFor="birthmonth" className="visually-hidden">
											月
										</label>
										<select
											id="birthmonth"
											name="birthMonth"
											className={isPlaceHolders.birthMonth ? classes.placeholderOption : ''}
											value={memberFormData.birthMonth}
											onChange={handleChangeSelectValueWithPlaceHolder}
										>
											<option value="" className={classes.placeholderOption}>
												月
											</option>
											{new Array(12).fill(0).map((v, i) => {
												return (
													<option value={`${i + 1}`} className={classes.defaultOption}>
														{i + 1}
													</option>
												);
											})}
										</select>
									</div>
								</div>
								<p className="error">{formErrors.birthMonth}</p>
								<div className="form-input width50">
									<div className="select-box">
										<label htmlFor="birthDate" className="visually-hidden">
											日
										</label>
										<select
											id="birthDate"
											name="birthDate"
											className={isPlaceHolders.birthDate ? classes.placeholderOption : ''}
											value={memberFormData.birthDate}
											onChange={handleChangeSelectValueWithPlaceHolder}
										>
											<option value="" className={classes.placeholderOption}>
												日
											</option>
											{new Array(31).fill(0).map((v, i) => {
												return (
													<option value={`${i + 1}`} className={classes.defaultOption}>
														{i + 1}
													</option>
												);
											})}
										</select>
									</div>
								</div>
								<p className="error">{formErrors.birthDate}</p>
							</div>
							<div className="form-box">
								<div className="form-head">
									<label>性別</label>
								</div>
								<div className="form-input">
									<div className="radio-box">
										<ul>
											<li>
												<label className={`radio-label${isMen ? ' is-active' : ''}`} htmlFor="male">
													<input
														type="radio"
														id="male"
														name="sex"
														value={0}
														checked={isMen}
														onChange={handleChangeRadioValue}
													/>
													男
												</label>
											</li>
											<li>
												<label className={`radio-label${isWomen ? ' is-active' : ''}`} htmlFor="female">
													<input
														type="radio"
														id="female"
														name="sex"
														value={1}
														checked={isWomen}
														onChange={handleChangeRadioValue}
													/>
													女
												</label>
											</li>
										</ul>
									</div>
								</div>
								<p className="error">{formErrors.sex}</p>
							</div>
							<div className="btn-box">
								<div className="btn gr">
									{processLoading ? <CircularProgress /> : <button onClick={validate}>確認</button>}
								</div>
							</div>
						</div>
					</>
				)}
				{page === 1 && (
					<>
						<h1 className="page-head">{`Gakken${GroupAuth.MEMBER.name}の登録`}</h1>
						<div className="section-in" style={{ marginTop: 50 }}>
							<div className="section-head">
								<span>入力内容の確認</span>
							</div>
							<p style={{ marginBottom: 40 }}>
								下記の内容をご確認いただき、よろしければ「登録」ボタンを押してください。
								<br />
								入力内容を変更したい場合は「戻る」ボタンから内容を変更してください。
							</p>
							<div className="info-box">
								<div className="info-head">GakkenID</div>
								<div className="info-txt">{memberFormData.gid}</div>
								<Hr />
								<div className="info-head">パスワード</div>
								<div className="info-txt">*************</div>
								<Hr />
								<div className="info-head">お名前（全角）</div>
								<div className="info-txt">{`${memberFormData.lastname}　${memberFormData.firstname}`}</div>
								<Hr />
								<div className="info-head">お名前（カナ）</div>
								<div className="info-txt">{`${memberFormData.kana_lastname}　${memberFormData.kana_firstname}`}</div>
								<Hr />
								<div className="info-head">生年月日</div>
								<div className="info-txt">
									<div className="info-txt">{`${memberFormData.birthYear}年　${memberFormData.birthMonth}月　${memberFormData.birthDate}日`}</div>
								</div>
								<Hr />
								<div className="info-head">性別</div>
								<div className="info-txt">{findSexTypeNameByKey(memberFormData.sex) || '　'}</div>

								<p className={classes.terms}>
									※お子様に代わり登録を行うことで
									<a href="/agreement" target="_blank">
										利用規約
									</a>
									に同意したとみなします。
								</p>

								<div className="btn-box">
									{processLoading ? (
										<CircularProgress />
									) : (
										<>
											<div className="btn wh">
												<button onClick={back}>戻る</button>
											</div>
											<div className="btn gr">
												<button onClick={register}>同意して登録</button>
											</div>
										</>
									)}
								</div>
							</div>
						</div>
					</>
				)}
				{page === 2 && (
					<div className="section-in" style={{ marginTop: 50 }}>
						<div className="thankyou-box">
							<img src="images/img-registered.svg" alt="登録が完了しました！" />
						</div>
						<p>
							<div>{`${GroupAuth.MEMBER.name}の登録が完了いたしました。`}</div>
							<div>登録した情報はマイページTOPの「家族⼀覧」より確認、変更が可能です。</div>
						</p>
						<div className="btn-box">
							<div className="btn gr">
								<Link to={RoutePath.MY_PAGE}>マイページTOPへ戻る</Link>
							</div>
						</div>
					</div>
				)}
			</section>
		</main>
	);
});
